import React, { FC, useEffect, useState } from 'react';

import DialogLayout from '../DialogLayout';
import { IStepperConfig } from '../../types/interfaces';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import ASSETS from '../../assets';
import Stepper from '../../components/Stepper/Stepper';
import { sleep } from '../../utils';

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  isUploadRoute?: boolean;
  finalized?: boolean;
  flow?: 'document' | 'text';
}

const CONFIG: IStepperConfig[] = [
  {
    text: 'Hashing',
    successText: 'Hashed',
    finished: false,
  },
  {
    text: 'Metadata',
    successText: 'Metadata',
    finished: false,
  },
  {
    text: 'Signing',
    successText: 'Signed',
    finished: false,
  },
  {
    text: 'Creating',
    successText: 'Created',
    finished: false,
  },
  {
    text: 'Finalizing',
    successText: 'Finalized',
    finished: false,
  },
];

const CONFIGTEXT: IStepperConfig[] = [
  {
    text: 'Details',
    successText: 'Details',
    finished: false,
  },

  {
    text: 'Signing',
    successText: 'Signed',
    finished: false,
  },
  {
    text: 'Creating',
    successText: 'Created',
    finished: false,
  },
  {
    text: 'Finalizing',
    successText: 'Finalized',
    finished: false,
  },
];

const GeneratingAttesation: FC<IProps> = ({
  open,
  setOpen,
  onClose,
  finalized,
  isUploadRoute = false,
  flow = 'document',
}) => {
  const [config, setConfig] = useState(flow === 'text' ? CONFIGTEXT : CONFIG);
  const [active, setActive] = useState(0);
  const [title, setTitle] = useState('Generating Attestation');
  const [description, setDescription] = useState(
    flow === 'text' ? 'Adding details…' : 'Document hashing……'
  );

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleGenerateAttesation = async () => {
    const isTextFlow = flow === 'text';
    const descriptions = isTextFlow
      ? ['Adding details…', 'Signing the text…', 'Creating attestation…', 'Finalizing…']
      : ['Document hashing……', 'Attaching metadata…', 'Signing the document…', 'Creating attestation…', 'Finalizing…'];

    const lastIndex = (isTextFlow ? CONFIGTEXT : CONFIG).length - 1;

    for (let stepIndex = 0; stepIndex <= lastIndex; stepIndex++) {
      setActive(stepIndex);
      setDescription(descriptions[stepIndex] ?? '');
      await sleep(2000);
      setConfig(prev => {
        const newConfig = [...prev];
        if (newConfig[stepIndex]) newConfig[stepIndex].finished = true;
        return newConfig;
      });
    }
  };

  useEffect(() => {
    const initialConfig = (flow === 'text' ? CONFIGTEXT : CONFIG).map(i => ({ ...i }));
    setConfig(initialConfig);
    setActive(0);
    setTitle('Generating Attestation');
    setDescription(flow === 'text' ? 'Adding details…' : 'Document hashing……');
    handleGenerateAttesation();
  }, [flow]);

  useEffect(() => {
    const lastIndex = config.length - 1;
    if (finalized && active === lastIndex) {
      setTitle('Congratulations! ');
      setDescription(
        'Your document has been successfully verified on the blockchain. There is now a permanent and immutable record of its creation.'
      );
      setConfig(prev => {
        const newConfig = [...prev];
        if (newConfig[lastIndex]) newConfig[lastIndex].finished = true;
        return newConfig;
      });
    }
  }, [finalized, active]);

  return (
    <DialogLayout open={open}>
      <div className='w-full max-w-[536px] bg-white p-[25px] rounded-[15px] max-h-[95vh] overflow-auto aspect-square flex flex-col'>
        <Stepper activeIndex={active} steps={config} />
        <div className='flex-grow flex flex-col justify-between items-center'>
          <div />
          <div className='w-full items-center flex flex-col px-[75px]'>
            <Typography variant='h3' color='black' className='title'>
              {title}
            </Typography>

            <Typography
              variant='h6'
              color='grey'
              className='text-center mt-[25px]'
            >
              {description}
            </Typography>
          </div>
          <div className='mb-10'>
            <Button
              leftIcon={ASSETS.doc_icon_white}
              label='View My Attestation'
              variant='primary'
              disabled={!config[config.length - 1]?.finished || !finalized}
              onClick={handleClose}
              className={!config[config.length - 1]?.finished || !finalized ? '!text-gray-500' : '!text-white'}
            />
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default GeneratingAttesation;
